/**
 * The overflow values that make a box a scroll container, and therefore the
 * containing block a `position: sticky` descendant pins against.
 *
 * `clip` is deliberately absent: it clips but never scrolls, so sticky ignores it.
 * Anvil2's own `Page` container is `overflow-x: clip`, and it sits between the shell
 * and whatever really scrolls in an embedded host — counting it would resolve a box
 * that never scrolls. `visible` is absent for the obvious reason. Everything else —
 * including `overlay`, which computes to `auto` — lands in this set.
 */
const SCROLLABLE_OVERFLOW = new Set(['auto', 'scroll', 'hidden']);

/** Whether a box's computed overflow makes it a scroll container. */
export const isScrollContainer = (overflowX: string, overflowY: string): boolean =>
    SCROLLABLE_OVERFLOW.has(overflowX) || SCROLLABLE_OVERFLOW.has(overflowY);

/** The flat-tree parent: slot assignment first, then the shadow host at a boundary. */
const flatTreeParent = (node: Node): Node | null =>
    (node as Element).assignedSlot ?? node.parentNode ?? (node as ShadowRoot).host ?? null;

/**
 * The scroll container `element` pins against, crossing shadow boundaries.
 *
 * Crossing them is not optional here: embedded in the monolith the shell is mounted
 * inside a `@servicetitan/web-components` custom element, so a plain `parentElement`
 * walk stops at the shadow root and reports the viewport for a page that is really
 * scrolled by a host pane.
 *
 * `null` means the viewport — which is also the answer for `<body>` and `<html>`,
 * never those elements themselves. Overflow set on either propagates to the viewport
 * (css-overflow-3 §3.3), so the viewport scrolls and the element does not; but that is
 * a *used*-value rule and `getComputedStyle` still reports the declared value, so an
 * overflow predicate alone cannot tell the two apart. Hosts do set it — Anvil2's own
 * `Page` writes `document.body.style.overflow` while the mobile sidebar popover is
 * open — and body's box starts at the top of the document, so mistaking it for the
 * scrollport would report a top edge that scrolls away with the page.
 */
export const resolveScrollport = (element: Element): Element | null => {
    let node: Node | null = flatTreeParent(element);

    while (node) {
        if (node === globalThis.document.body || node === globalThis.document.documentElement) {
            return null;
        }
        if (node.nodeType === Node.ELEMENT_NODE) {
            const style = globalThis.getComputedStyle(node as Element);
            if (isScrollContainer(style.overflowX, style.overflowY)) {
                return node as Element;
            }
        }
        node = flatTreeParent(node);
    }

    return null;
};
